import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import styles from './ShortcutQR.scss'
import { getExperienceId } from '../ExperiencesApp/ExperiencesApp'
import { UnlimitedCodes } from './UnlimitedCodes'
import { LimitedCodes } from './LimitedCodes'
import { ColWrap, MainCol } from 'common/layouts/SideNavLayout/TwoCol'
import { Card } from 'common/styleguide/Card'
import { RadioButtonGroup } from 'common/styleguide/RadioGroup'
import { Loadable } from 'common/Loadable'
import { fm, commonMessages } from 'lib/localization'
import { experienceActions, experienceSelectors } from 'data/resources/experiences'
import { appCodeActions, appCodeSelectors } from 'data/resources/appCodes'
import { FormattedMessage as T, defineMessages, injectIntl } from 'react-intl'
import { OrderedMap } from 'immutable'

const messages = defineMessages({
  unlimited: {
    id: 'ShortcutQR.unlimited',
    defaultMessage: 'Unlimited use'
  },
  limited: {
    id: 'ShortcutQR.limited',
    defaultMessage: 'Limited use'
  }
})

const limitedOpts = intl => OrderedMap({
  true: fm(intl, messages.unlimited),
  false: fm(intl, messages.limited)
})

export const _ShortcutQR = ({
  experience,
  updateExperience,
  hasGuideShortcut,
  intl,
  ...props
}) => {
  const [unlimitedCodes, setUnlimitedCodes] = useState(experience.unlimited)

  useEffect(() => {
    if (experience.unlimited !== unlimitedCodes) {
      setUnlimitedCodes(experience.unlimited)
    }
  }, [experience])

  const save = unlimited => {
    const nextExperience = experience.merge({ unlimited })
    updateExperience(nextExperience)
  }

  return (
    <ColWrap>
      <MainCol>
        <h2 data-heading-alt>
          <T
            id='ShortcutQR.pageHeading'
            defaultMessage='Shortcut Code & QR'
            description="QR refers to 'Quick Response Code', a type of two dimensional barcode" />
        </h2>
        <Card>
          <LimitedUnlimited unlimitedCodes={unlimitedCodes} onChange={key => save(key === 'true')} />

          <hr />

          {unlimitedCodes
            ? <UnlimitedCodes
              experience={experience}
              hasGuideShortcut={hasGuideShortcut}
              {...props} />
            : <LimitedCodes
              experience={experience}
              {...props} />}

        </Card>
      </MainCol>
    </ColWrap>
  )
}

const loadResources = (props, experienceId) => {
  const { loadExperience, loadAppCodes, loadAppCode } = props
  return Promise.all([
    loadExperience(),
    loadAppCodes(),
    loadAppCode()
  ])
}

export const LoadableShortcutQR = (props) => {
  const experienceId = getExperienceId(props)
  return <Loadable
    {...props}
    experienceId={experienceId}
    component={_ShortcutQR}
    loadingText={fm(props.intl, commonMessages.loading)}
    resourceLoaded={() => loadResources(props, experienceId)} />
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const experienceId = getExperienceId(ownProps)
  return {
    loadExperience: () => dispatch(experienceActions.read(experienceId)),
    updateExperience: params => dispatch(experienceActions.update(params)),
    updateCode: (code, guideCode) => dispatch(experienceActions.updateCode(experienceId, { code, guideCode })),
    generateAppCode: guideCode => dispatch(experienceActions.generateAppCode(experienceId, { guideCode })),
    loadAppCodes: () => dispatch(appCodeActions.list({ parentType: 'Experience', parentId: experienceId })),
    loadAppCode: () => dispatch(experienceActions.appCode(experienceId, { extras: 'guide_code' })),
    updateAppCode: (appCodeId, params) => dispatch(appCodeActions.update(appCodeId, params)),
    createAppCodes: count => dispatch(appCodeActions.bulkCreate({ parentType: 'Experience', parentId: experienceId, count })),
    deleteAppCode: appCodeId => dispatch(appCodeActions.delete(appCodeId))
  }
}

const mapStateToProps = (state, ownProps) => {
  const experienceId = getExperienceId(ownProps)
  return {
    experience: experienceSelectors.find(state, experienceId),
    hasGuideShortcut: experienceSelectors.hasGuideShortcut(state, experienceId),
    appCode: experienceSelectors.appCode(state, experienceId),
    guideCode: experienceSelectors.guideCode(state, experienceId),
    appCodes: appCodeSelectors.byExperienceId(state, experienceId).sort((a, b) => a.createdAt > b.createdAt ? -1 : 1)
  }
}

export const ShortcutQR = connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(LoadableShortcutQR))

const LimitedUnlimited = injectIntl(({ unlimitedCodes, onChange, intl }) => (
  <div>
    <h3>
      <T
        id='ShortcutQR.setAccess'
        defaultMessage='Set Access'
        description='Allows the user to choose how their asset is accessed' />
    </h3>
    <T
      id='ShortcutQR.chooseBetween'
      defaultMessage='Choose between limited or unlimited use codes for your Experience.' />
    <div className={styles.horizontalOpts}>
      <RadioButtonGroup
        inline
        selected={`${unlimitedCodes}`}
        options={limitedOpts(intl)}
        onChange={onChange} />
    </div>
  </div>
))
